import { Component, h, Prop, Element, Host } from '@stencil/core';
import warn from '../../../../capabilities/warn';
import FormControl from '../_interfaces/FormControl';

/**
 * @description form-kit: Applies Design System styles to a slotted label.
 *
 * @use
 * @version 2
 * @formkit
 * @since 5.29.2
 * @slot - `default slot`: The label element to be decorated.
 *
 */
@Component({
  tag: 'hp6-label-decorator',
  styleUrl: 'label-decorator.scss',
  shadow: true,
})
export class LabelDecorator {
  /**
   * Will be passed to the <hp6-field-optional text="" /> attribute
   */
  @Prop() fieldOptionalText: string = null;

  /**
   * Alignment left or center
   *
   * Used as prop rather than attribute in input-text, so reflect: true is needed
   */
  @Prop({ reflect: true }) alignmentSelection: 'left' | 'center' = 'left';

  /**
   * Boolean if <hp6-field-optional /> is to be shown
   * Not relevant if the the <input /> has required="true"
   *
   * Used as prop rather than attribute in input-text, so reflect: true is needed
   */
  @Prop({ reflect: true }) showFieldOptional: boolean = true;

  /**
   * Size of the label.
   *
   * Used as prop rather than attribute in input-text, so reflect: true is needed
   */
  @Prop({ reflect: true }) sizeSelection: 'xs' | 'sm' | 'md' | 'lg' = 'xs';

  /**
   * Font type of the label
   *
   * Used as prop rather than attribute in input-text, so reflect: true is needed
   */
  @Prop({ reflect: true }) typeSelection: 'heading' | 'body' = 'heading';

  /**
   * A reference to the host element
   */
  @Element() host: HTMLElement;

  /**
   * The <input /> <select /> etc. in this <slot />
   * Memoized in componentWillLoad()
   */
  private formControl: FormControl | Element;

  private label: HTMLLabelElement;

  get rootNode(): ShadowRoot | Document {
    return this.host.getRootNode() as ShadowRoot | Document;
  }

  /**
   * Stencil lifecycle
   *
   * @returns void
   */
  componentWillLoad() {
    this.label = this.host.querySelector('label');

    if (!this.label) {
      warn('label-decorator: label-decorator cannot find label');
      return;
    }

    if (!this.label.htmlFor) {
      warn('label-decorator: label does not have a "for" attribute');
      return;
    }

    const formControl = this.rootNode.querySelector(`#${this.label.htmlFor}`);
    if (!formControl) {
      warn('label-decorator: "for" attribute does not match a form control');
      return;
    }

    this.formControl = formControl;
  }

  /**
   * this.required getter
   * Returns the required="" attribute value from this.formControl
   *
   * @returns boolean
   */
  get required(): boolean {
    const { formControl } = this;

    if (!formControl) {
      warn('label-decorator: required() form control not found');
      return false;
    }

    const attribute = formControl.hasAttribute('required');

    const required = attribute ? true : false;
    return required;
  }

  componentDidRender() {
    const { label, required, showFieldOptional } = this;

    if (label && !required) {
      // insert <hp6-field-optional /> if required is false
      const optionalElement = this.host.querySelector('hp6-field-optional');
      if (showFieldOptional && !optionalElement) {
        const { fieldOptionalText } = this;
        const fieldOptionalElement = document.createElement('hp6-field-optional');
        if (fieldOptionalText) {
          fieldOptionalElement.setAttribute('text', fieldOptionalText);
        }
        label.appendChild(fieldOptionalElement);
      }
      // remove <hp6-field-optional /> if required is true
      if (!showFieldOptional && optionalElement) {
        optionalElement.remove();
      }
    }
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    return (
      <Host>
        <slot />
      </Host>
    );
  }
}
